import { useEffect, useMemo, type ReactElement } from 'react';
import { Badge, Center, Loader, Text } from '@mantine/core';
import { useViewModel } from '@/shared/viewmodels/useViewModel';
import MegaGrid, { type MegaGridColumn } from '@/design/MegaGrid/MegaGrid';
import { useGlobalFilters } from '@/features/global-filters/useGlobalFilters';
import { resolveDateRange } from '@/features/global-filters/GlobalFilterTypes';
import { GovernanceActivityViewModel, type ActivityRow } from './GovernanceActivityViewModel';
import { formatWhen } from './PolicyFormatting';

const CATEGORY_COLOR: Record<ActivityRow['category'], string> = { Triage: 'cyan', Policy: 'grape' };

const COLUMNS: MegaGridColumn<ActivityRow>[] = [
    {
        key: 'when',
        header: 'When',
        width: 170,
        sortValue: (r) => new Date(r.when),
        render: (r) => (
            <Text size="xs" c="dimmed" ff="monospace">
                {formatWhen(r.when)}
            </Text>
        ),
    },
    {
        key: 'category',
        header: 'Type',
        sortValue: (r) => r.category,
        groupValue: (r) => r.category,
        filter: { type: 'enum', accessor: (r) => r.category },
        render: (r) => (
            <Badge size="xs" radius="sm" variant="light" color={CATEGORY_COLOR[r.category]}>
                {r.category}
            </Badge>
        ),
    },
    {
        key: 'action',
        header: 'Action',
        width: 150,
        sortValue: (r) => r.action,
        groupValue: (r) => r.action,
        filter: { type: 'enum', accessor: (r) => r.action },
        render: (r) => (
            <Text size="xs" fw={600} truncate>
                {r.action}
            </Text>
        ),
    },
    {
        key: 'target',
        header: 'Target',
        width: 220,
        sortValue: (r) => r.target,
        groupValue: (r) => r.target,
        render: (r) => (
            <Text size="xs" truncate>
                {r.target}
            </Text>
        ),
    },
    {
        key: 'detail',
        header: 'Detail',
        width: 220,
        sortValue: (r) => r.detail,
        render: (r) => (
            <Text size="xs" c="dimmed" truncate>
                {r.detail}
            </Text>
        ),
    },
    {
        key: 'actor',
        header: 'By',
        width: 180,
        sortValue: (r) => r.actor,
        groupValue: (r) => r.actor,
        render: (r) => (
            <Text size="xs" c="dimmed" truncate>
                {r.actor}
            </Text>
        ),
    },
];

/**
 * The Activity tab: a reverse-chronological feed of governance actions — triage on issues and
 * policy changes. The audit feed isn't an OLAP query, so the global date range is applied
 * client-side to the loaded rows; the feed is capped at its newest N before this filter, so very
 * old ranges may show little (recent-activity bias).
 */
export default function GovernanceActivityTab(): ReactElement {
    const vm = useViewModel(GovernanceActivityViewModel);
    const { snapshot } = useGlobalFilters();

    useEffect(() => {
        void vm.initialize();
    }, [vm]);

    const rows = useMemo(() => {
        const { start, end } = resolveDateRange(snapshot.dateRange);
        const from = start.getTime();
        const to = end.getTime();
        return vm.rows.filter((row) => {
            const at = new Date(row.when).getTime();
            return at >= from && at <= to;
        });
    }, [vm.rows, snapshot.dateRange]);

    if (!vm.loaded) {
        return (
            <Center py="xl">
                <Loader size="sm" />
            </Center>
        );
    }

    if (vm.errorMessage !== null) {
        return (
            <Text c="red" size="sm">
                Couldn't load activity: {vm.errorMessage}
            </Text>
        );
    }

    return (
        <MegaGrid
            columns={COLUMNS}
            rows={rows}
            getRowKey={(r) => r.id}
            persistenceKey="governance-activity-v2"
            searchAccessor={(r) => `${r.action} ${r.target} ${r.detail} ${r.actor} ${r.category}`}
            searchPlaceholder="Search activity"
            striped
            density="compact"
            minWidth={640}
            clientPageSize={30}
            emptyMessage="No governance activity yet."
        />
    );
}
